import { Injectable, OnDestroy } from '@angular/core';
import { Subscription, interval } from 'rxjs';
import { FleetMockStore } from './fleet-mock.store';
import { FleetVehicle } from './models/fleet.models';

/**
 * Simulador de movimiento — da la ilusión de un "mapa vivo" durante las demos.
 * Cada tick mueve ligeramente sólo los vehículos en estado MOVING; los
 * STOPPED / IDLE quedan fijos para que el semáforo del mapa sea coherente.
 *
 * Idiomático Angular: RxJS interval() en vez de setInterval crudo. Se arranca
 * desde el FleetCommandCenter y se detiene con stop()/ngOnDestroy para no
 * fugar timers.
 */
@Injectable({ providedIn: 'root' })
export class FleetSimulatorService implements OnDestroy {
  private sub?: Subscription;
  private running = false;

  constructor(private readonly store: FleetMockStore) {
    // do nothing
  }

  get isRunning(): boolean {
    return this.running;
  }

  start(periodMs = 2000): void {
    if (this.running) return;
    this.running = true;
    this.sub = interval(periodMs).subscribe(() => this.tick());
  }

  stop(): void {
    this.sub?.unsubscribe();
    this.sub = undefined;
    this.running = false;
  }

  private tick(): void {
    const next = this.store.snapshot().map((v) => (v.status_live === 'MOVING' ? this.advance(v) : v));
    this.store.setAll(next);
  }

  private advance(v: FleetVehicle): FleetVehicle {
    const jitter = () => (Math.random() - 0.5) * 0.0016; // ~150 m de deriva
    const speed = this.clamp(v.telemetry.speedKmh + (Math.random() - 0.5) * 10, 10, 120);
    const heading = (v.telemetry.heading + (Math.random() - 0.5) * 24 + 360) % 360;
    const fuel = this.clamp(v.telemetry.fuelPct - Math.random() * 0.2, 0, 100);
    const odometerKm = v.telemetry.odometerKm + speed / 1800; // aprox por tick
    return {
      ...v,
      km: Math.round(odometerKm),
      telemetry: {
        ...v.telemetry,
        lat: v.telemetry.lat + jitter(),
        lng: v.telemetry.lng + jitter(),
        speedKmh: Math.round(speed),
        heading: Math.round(heading),
        fuelPct: Math.round(fuel),
        odometerKm: Math.round(odometerKm),
        updatedAt: new Date().toISOString(),
      },
    };
  }

  private clamp(n: number, min: number, max: number): number {
    return Math.min(Math.max(n, min), max);
  }

  ngOnDestroy(): void {
    this.stop();
  }
}
